import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  Alert,
  Chip,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import ClassFormModal from "../../../components/Modals/ClassFormModal";
import EnhancedTable from "../../../components/common/EnhancedTable";
import { useAuth } from "../../../contexts/AuthContext";
import { useError } from "../../../contexts/ErrorContext";
import { useClasses, ClassData } from "../../../hooks/useClasses";
import { useStudents } from "../../../hooks/useStudents";
import { useTeacher } from "../../../hooks/useTeacher";
import { useSessionClass } from "../../../contexts/SessionClassContext";

const getClassSortValue = (className?: string) => {
  if (!className) return Number.MAX_SAFE_INTEGER;

  const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  // Ensure Nursery -> LKG -> UKG always appear before Class 1.
  if (normalized.includes("nursery")) return -3;
  if (normalized.includes("lkg")) return -2;
  if (normalized.includes("ukg")) return -1;

  const numericMatch = className.match(/\d+/);
  if (numericMatch) return parseInt(numericMatch[0], 10);

  // Non-numeric/unknown class names go to the end.
  return Number.MAX_SAFE_INTEGER;
};

// Helper function to get section sort value (A=1, B=2, C=3, etc.)
const getSectionSortValue = (section?: string | null) => {
  if (!section) return 0;
  const normalized = section.trim().toUpperCase();
  if (normalized.length === 0) return 0;
  // Get the first character's ASCII value (A=65, B=66, etc.)
  const charCode = normalized.charCodeAt(0);
  if (charCode >= 65 && charCode <= 90) {
    return charCode - 64; // A=1, B=2, C=3, etc.
  }
  return Number.MAX_SAFE_INTEGER; // Non-letter sections go to end
};

const getSessionSortValue = (sessionName?: string) => {
  if (!sessionName) return Number.MAX_SAFE_INTEGER;
  const yearMatch = sessionName.match(/(\d{4})/);
  if (yearMatch) {
    return parseInt(yearMatch[1], 10);
  }
  return Number.MAX_SAFE_INTEGER;
};

const ClassManagement: React.FC = () => {
  const { user, isTeacher } = useAuth();
  const isTeacherUser = isTeacher?.() ?? false;

  // For both teachers and school admins, use their schoolId directly from user context
  // This ensures we use the actual schoolId from the logged-in user
  const schoolId = user?.schoolId;


  const { teachersQuery } = useTeacher();
  const { studentsQuery } = useStudents();
  const { selectedClass, setSelectedClass, selectedSession } = useSessionClass();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showError } = useError();
  const {
    classesQuery,
    createClassMutation,
    updateClassMutation,
    deleteClassMutation,
  } = useClasses(schoolId ?? undefined);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentClass, setCurrentClass] = useState<ClassData | undefined>(
    undefined
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<ClassData | null>(null);

  const formatClassLabel = (classData: ClassData) =>
    `${classData.name ?? ""} ${classData.section ?? ""}`.trim();

  const handleOpenAddModal = () => {
    if (isTeacherUser) {
      showError(t("no_permission_to_perform_action"));
      return;
    }
    setIsEdit(false);
    setCurrentClass(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (classData: any) => {
    // Wait for data to load before checking

    // Check if there are students in this class (with same session)
    const studentsInClass = studentsQuery.data?.filter(
      (student) =>
        student.className === classData.name &&
        student.section === classData.section &&
        student.sessionId === classData.sessionId &&
        student.status !== false
    ) || [];

    // Prevent editing if there are students
    if (studentsInClass.length > 0) {
      showError(
        t("cannot_edit_class_due_to_dependencies", {
          className: formatClassLabel(classData),
          dependencies: `${studentsInClass.length} ${t("students")}`,
        })
      );
      return;
    }

    setIsEdit(true);
    // Remove computed fields before setting as current class
    const { sessionName, sessionStatus, isSessionActive, ...cleanClassData } = classData;
    setCurrentClass(cleanClassData as ClassData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = (formData: ClassData) => {
    if (isEdit && currentClass?.id) {
      // Remove any computed fields that might have been added for display
      const { sessionName, sessionStatus, isSessionActive, ...cleanFormData } = formData as any;

      // Check if the class being updated is currently selected
      const isSelectedClass = selectedClass?.id === currentClass.id;

      updateClassMutation.mutate(
        { id: currentClass.id, data: cleanFormData as ClassData },
        {
          onSuccess: (updatedClass) => {
            handleCloseModal();

            // If the updated class was selected, update the selection with new data
            if (isSelectedClass) {
              setSelectedClass(updatedClass);
            }

            queryClient.invalidateQueries({ queryKey: ["classes"] });
          },
          onError: (error) => {
          },
        }
      );
    } else {
      if (isTeacherUser) {
        showError(t("no_permission_to_perform_action"));
        return;
      }
      // For create, also ensure no computed fields are sent
      const { sessionName, sessionStatus, isSessionActive, ...cleanFormData } = formData as any;
      createClassMutation.mutate(cleanFormData as ClassData, {
        onSuccess: () => {
          handleCloseModal();
          queryClient.invalidateQueries({ queryKey: ["classes"] });
        },
        onError: (error: any) => {
          const backendMessage =
            error?.response?.data?.message ||
            error?.message ||
            t("error_creating_class");
          showError(backendMessage);
        },
      });
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    // Find the class data
    const classData = classesQuery.data?.find((cls) => cls.id === id);
    if (!classData) {
      showError(t("class_not_found"));
      return;
    }

    // Check if there are students in this class (with same session)
    const studentsInClass = studentsQuery.data?.filter(
      (student) =>
        student.className === classData.name &&
        student.section === classData.section &&
        student.sessionId === classData.sessionId &&
        student.status !== false
    ) || [];
    if (studentsInClass.length > 0) {
      showError(
        t("cannot_delete_class_due_to_dependencies", {
          className: formatClassLabel(classData),
          dependencies: `${studentsInClass.length} ${t("students")}`,
        })
      );
      return;
    }

    // If no students, allow deletion
    setClassToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setClassToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (classToDelete !== null) {
      // Check if the class being deleted is currently selected
      const isSelectedClass = selectedClass?.id === classToDelete;

      deleteClassMutation.mutate(classToDelete, {
        onSuccess: () => {
          handleCloseDeleteDialog();

          // If the deleted class was selected, clear the selection
          if (isSelectedClass) {
            setSelectedClass(null);
          }

          queryClient.invalidateQueries({ queryKey: ["classes"] });
        },
        onError: (error: any) => {
          const backendMessage = error?.response?.data?.message || "";
          if (backendMessage.includes("referenced by other data")) {
            showError(t("cannot_delete_class_dependencies"));
          } else {
            showError(backendMessage || t("something_went_wrong"));
          }
        },
      });
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: ClassData
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Format class data with session information
  const formattedClassData = useMemo(() => {
    if (!classesQuery.data) return [];

    return classesQuery.data
      .map((classItem) => {
        // Use selectedSession from context if it matches, otherwise use sessionId
        const sessionName = selectedSession && selectedSession.id === classItem.sessionId
          ? selectedSession.name
          : t("not_available");
        const isSessionActive = selectedSession && selectedSession.id === classItem.sessionId
          ? selectedSession.isActive
          : false;

        return {
          ...classItem,
          sessionName: sessionName,
          sessionStatus: isSessionActive ? t("active") : t("inactive"),
          isSessionActive: isSessionActive,
        };
      })
      .sort((a, b) => {
        // First sort by class number (8, 9, 10, etc.)
        const classDiff = getClassSortValue(a.name) - getClassSortValue(b.name);
        if (classDiff !== 0) return classDiff;

        // Then sort by section letter (A, B, C, D, etc.)
        const sectionDiff = getSectionSortValue(a.section) - getSectionSortValue(b.section);
        if (sectionDiff !== 0) return sectionDiff;

        // Then sort by session
        const sessionDiff =
          getSessionSortValue(a.sessionName) - getSessionSortValue(b.sessionName);
        if (sessionDiff !== 0) return sessionDiff;

        // Final fallback to alphabetical
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [classesQuery.data, selectedSession, t]);

  const columns = [
    { id: "name", label: t("class_name"), minWidth: 170 },
    { id: "section", label: t("section"), minWidth: 100 },
  ];

  const renderActions = (row: ClassData) => (
    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
      <MoreVertIcon />
    </IconButton>
  );

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        {(!selectedSession || !selectedSession.isActive) && (
          <Alert severity="warning" sx={{ mb: 2, backgroundColor: "#fff3cd", color: "#856404" }}>
            {t("please_activate_your_session_to_make_class")}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("class_management")}
          </Typography>
          {!isTeacherUser && (
            <Button
              variant="contained"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleOpenAddModal}
              disabled={!selectedSession?.isActive}
            >
              {t("add_class")}
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <EnhancedTable
            columns={columns}
            data={formattedClassData}
            searchFields={["name", "section"]}
            actions={renderActions}
            cardProps={{
              elevation: 0,
              sx: {
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              if (menuRow) handleOpenEditModal(menuRow);
              handleMenuClose();
            }}
          >
            {t("edit")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuRow) handleOpenDeleteDialog(menuRow.id!);
              handleMenuClose();
            }}
            sx={{ color: "red" }}
          >
            {t("delete")}
          </MenuItem>
        </Menu>

        <ClassFormModal
          open={modalOpen}
          handleClose={handleCloseModal}
          initialData={currentClass}
          onSubmit={handleSubmit}
          isEdit={isEdit}
          schoolId={schoolId ?? 0}
          teachers={teachersQuery.data || []}
          students={studentsQuery.data || []}
          existingClasses={classesQuery.data || []}
        />

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t("confirm_delete")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("are_you_sure_you_want_to_delete_this_class")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}>
              {t("cancel")}
            </Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              {t("delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  );
};

export default ClassManagement;